import { OTP_LENGTH } from "@car/auth/otp";
import { Button } from "@car/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@car/ui/components/card";
import { FormAlert, statusControlClass, useAppForm } from "@car/ui/components/form";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@car/ui/components/input-otp";
import { Spinner } from "@car/ui/components/spinner";
import { mobileText, normalizeMobile } from "@car/ui/lib/form-rules";
import { cn } from "@car/ui/lib/utils";
import { ArrowRight01Icon, SmartphoneIcon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";

import { authClient, isCustomerUser } from "@/lib/auth-client";
import { toEnDigits, toFaDigits } from "@/lib/format";
import { authErrorMessage } from "@/lib/panel";

// A staff session that opens /panel is sent here too (panel/route.tsx) and simply sees the
// customer login; a staff phone that verifies here is signed out again with a refusal (verify).
const loginSearchSchema = z.object({
  redirect: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/login")({
  ssr: false,
  validateSearch: loginSearchSchema,
  beforeLoad: async ({ search }) => {
    // سرور در دسترس نباشد هم صفحه ورود باید باز شود
    const session = await authClient.getSession().catch(() => null);
    if (session?.data && isCustomerUser(session.data.user)) {
      throw redirect({ to: search.redirect ?? "/panel" });
    }
  },
  head: () => ({
    meta: [
      { title: "ورود به پنل مشتریان | تابان خودرو" },
      {
        name: "description",
        content: "ورود و ثبت‌نام یکپارچه با شماره موبایل و کد تأیید پیامکی.",
      },
    ],
  }),
  component: LoginPage,
});

const otpSlots = Array.from({ length: OTP_LENGTH }, (_, index) => index);
/** فاصله مجاز بین دو درخواست کد پیامکی */
const RESEND_AFTER_SECONDS = 60;

const phoneSchema = z.object({ phone: mobileText() });

const OTP_PATTERN = new RegExp(`^\\d{${OTP_LENGTH}}$`);

/** کد پیامکی: مقدار فیلد همیشه با ارقام لاتین نگه داشته می‌شود */
const codeSchema = z.object({
  code: z.string().superRefine((value, context) => {
    if (value === "") {
      context.addIssue({ code: "custom", message: "کد تأیید را وارد کنید" });
    } else if (!OTP_PATTERN.test(value)) {
      context.addIssue({
        code: "custom",
        message: `کد تأیید باید ${toFaDigits(String(OTP_LENGTH))} رقم باشد`,
      });
    }
  }),
});

function LoginPage() {
  const navigate = useNavigate();
  const { redirect: redirectTo } = Route.useSearch();
  const [sentTo, setSentTo] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setTimeout(() => setSecondsLeft((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  const phoneForm = useAppForm({
    defaultValues: { phone: "" },
    validators: { onBlur: phoneSchema },
    // خطای سرور با ویرایش شماره پاک می‌شود
    listeners: { onChange: () => setError("") },
    onSubmit: ({ value }) => sendCode(normalizeMobile(value.phone)),
  });

  const codeForm = useAppForm({
    defaultValues: { code: "" },
    validators: { onBlur: codeSchema },
    onSubmit: ({ value }) => verify(value.code),
  });

  // پس از کد نادرست، خانه‌ها خالی و فوکوس (و صفحه‌کلید گوشی) همان‌جا می‌ماند
  const codeInput = useRef<HTMLInputElement>(null);
  const retryCode = (message: string) => {
    setPending(false);
    codeForm.reset();
    setError(message);
    codeInput.current?.focus();
  };

  /** شماره پیش‌تر با mobileText (فرم) یا در ارسال قبلی (ارسال دوباره) بررسی شده است */
  const sendCode = async (target: string) => {
    setError("");
    setPending(true);
    const result = await authClient.phoneNumber.sendOtp({ phoneNumber: target });
    setPending(false);
    if (result.error) {
      setError(authErrorMessage(result.error, "ارسال کد انجام نشد؛ دوباره تلاش کنید."));
      return;
    }
    codeForm.reset();
    setSentTo(target);
    setSecondsLeft(RESEND_AFTER_SECONDS);
    // ارسال دوباره: کاربر مستقیم کد تازه را وارد می‌کند (بار اول autoFocus کار را می‌کند)
    codeInput.current?.focus();
  };

  const verify = async (code: string) => {
    if (pending) return;
    setError("");
    setPending(true);
    const result = await authClient.phoneNumber.verify({ phoneNumber: sentTo, code });
    if (result.error) {
      retryCode(authErrorMessage(result.error, "کد واردشده نادرست است یا منقضی شده."));
      return;
    }
    // یک حساب کارمند با همین شماره وارد پنل مشتریان نمی‌شود
    const session = await authClient.getSession().catch(() => null);
    if (!session?.data || !isCustomerUser(session.data.user)) {
      await authClient.signOut().catch(() => null);
      retryCode("این شماره متعلق به حساب کارکنان است و به پنل مشتریان دسترسی ندارد.");
      return;
    }
    navigate({ to: redirectTo ?? "/panel" });
  };

  return (
    <main className="grid min-h-[100dvh] place-items-center bg-muted/40 px-4 py-10">
      <div className="w-full max-w-sm">
        <a
          href="/"
          className="mb-6 flex items-center justify-center gap-2"
          aria-label="تابان خودرو، صفحه اصلی"
        >
          <img
            src="/logo.webp"
            alt=""
            width={320}
            height={227}
            className="h-11 w-auto dark:brightness-0 dark:invert"
          />
          <span className="text-xl font-black">تابان خودرو</span>
        </a>

        <Card>
          {!sentTo ? (
            <phoneForm.AppForm>
              <phoneForm.Form>
                <CardHeader>
                  <CardTitle as="h1">ورود به پنل مشتریان</CardTitle>
                  <CardDescription>
                    شماره موبایل خود را وارد کنید؛ ورود و ثبت‌نام از همین‌جا انجام می‌شود.
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-2 pt-4">
                  <phoneForm.AppField name="phone">
                    {(field) => (
                      <field.TextField
                        label={
                          <>
                            <HugeiconsIcon
                              icon={SmartphoneIcon}
                              strokeWidth={1.8}
                              aria-hidden="true"
                              className="size-4.5 text-muted-foreground"
                            />
                            شماره موبایل
                          </>
                        }
                        description={`کد تأیید ${toFaDigits(String(OTP_LENGTH))} رقمی به همین شماره پیامک می‌شود.`}
                        type="tel"
                        dir="ltr"
                        inputMode="tel"
                        autoComplete="tel"
                        autoFocus
                        placeholder="مثلاً ۰۹۱۲۱۲۳۴۵۶۷"
                        className="text-start"
                      />
                    )}
                  </phoneForm.AppField>
                  <FormAlert message={error} />
                </CardContent>
                <CardFooter className="grid gap-3 pt-4">
                  <phoneForm.SubmitButton size="lg" className="w-full" pending={pending}>
                    دریافت کد تأیید
                  </phoneForm.SubmitButton>
                  <p className="text-center text-xs text-muted-foreground">
                    پیش از ورود می‌توانید{" "}
                    <a href="/terms" className="font-bold underline underline-offset-4">
                      شرایط استفاده
                    </a>{" "}
                    و{" "}
                    <a href="/privacy" className="font-bold underline underline-offset-4">
                      حریم خصوصی
                    </a>{" "}
                    را بخوانید.
                  </p>
                </CardFooter>
              </phoneForm.Form>
            </phoneForm.AppForm>
          ) : (
            <codeForm.AppForm>
              <codeForm.Form>
                <CardHeader>
                  <CardTitle as="h1">کد تأیید را وارد کنید</CardTitle>
                  <CardDescription>
                    کد {toFaDigits(String(OTP_LENGTH))} رقمی برای شماره{" "}
                    <span dir="ltr" className="font-bold text-foreground">
                      {toFaDigits(sentTo)}
                    </span>{" "}
                    پیامک شد و دو دقیقه اعتبار دارد.
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 pt-4">
                  <codeForm.AppField name="code">
                    {(field) => (
                      <field.FormField label="کد تأیید پیامکی">
                        {({ controlProps, status }) => (
                          <div dir="ltr" className="flex justify-center">
                            <InputOTP
                              {...controlProps}
                              maxLength={OTP_LENGTH}
                              value={field.state.value}
                              onChange={(next) => {
                                // فقط رقم (فارسی هم پذیرفته می‌شود)؛ حرف و نماد پذیرفته نمی‌شود
                                const code = toEnDigits(next).replace(/\D/g, "");
                                field.handleChange(code);
                                // مثل قبل: کد کامل خودش ارسال می‌شود
                                if (code.length === OTP_LENGTH) {
                                  field.handleBlur();
                                  void codeForm.handleSubmit();
                                }
                              }}
                              ref={codeInput}
                              autoFocus
                              autoComplete="one-time-code"
                              // disabled فوکوس و صفحه‌کلید را می‌گیرد؛ در زمان بررسی فقط قفل است
                              readOnly={pending}
                              aria-busy={pending}
                            >
                              <InputOTPGroup>
                                {otpSlots.map((index) => (
                                  <InputOTPSlot
                                    key={index}
                                    index={index}
                                    aria-invalid={controlProps["aria-invalid"]}
                                    className={cn("size-11 text-base", statusControlClass(status))}
                                  />
                                ))}
                              </InputOTPGroup>
                            </InputOTP>
                          </div>
                        )}
                      </field.FormField>
                    )}
                  </codeForm.AppField>
                  <FormAlert message={error} className="text-center" />
                  {pending ? (
                    <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                      <Spinner className="size-4" />
                      در حال بررسی کد و ورود به پنل…
                    </p>
                  ) : null}
                  <p className="text-center text-xs text-muted-foreground">
                    اگر پیامک نرسید، پس از پایان شمارش کد تازه بگیرید. اولین ورود با هر شماره، حساب
                    مشتری شما را می‌سازد.
                  </p>
                </CardContent>
                <CardFooter className="flex-wrap justify-between gap-2 pt-4">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={pending}
                    onClick={() => {
                      setSentTo("");
                      codeForm.reset();
                      setError("");
                    }}
                  >
                    <HugeiconsIcon icon={ArrowRight01Icon} strokeWidth={1.8} />
                    اصلاح شماره موبایل
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={pending || secondsLeft > 0}
                    onClick={() => sendCode(sentTo)}
                  >
                    {secondsLeft > 0
                      ? `ارسال دوباره تا ${toFaDigits(String(secondsLeft))} ثانیه دیگر`
                      : "ارسال دوباره کد"}
                  </Button>
                </CardFooter>
              </codeForm.Form>
            </codeForm.AppForm>
          )}
        </Card>
      </div>
    </main>
  );
}
